import { parseInlineLinkMarkers } from '@source-components-react/shared/links';

/**
 * The attribute names `r-accordion` reads.
 *
 * The component is a disclosure and nothing more specific than that: a summary
 * a reader clicks and the detail it expands to. An FAQ is one thing to build
 * out of it, a shipping policy or a "show more" under a truncated bio are
 * others, and the settings are named after the two halves of the element —
 * `<summary>` and the detail inside `<details>` — rather than after any one of
 * those uses.
 *
 * One accordion is one disclosure, rather than a component holding N
 * collapsible sections. A repeating-group attribute would need an editor this
 * library does not have, and several sections in one component leave the
 * nested-content slot with no single detail to belong to (see
 * `rulecms/__docs__/__ARCHITECTURE__/ACCORDION_COMPONENT_ARCHITECTURE.md`).
 * Several of these in a column is the multi-section case, which is also how an
 * author reorders one.
 *
 * Every name is accordion-specific, including the links, which are the same
 * numbered list `r-text` already offers. Sharing that attribute name would
 * have saved a line of editor wiring and cost the documentation contract:
 * RuleCMS publishes one page per component-scope attribute and requires
 * exactly one owning component. Reuse happens a level down, where the composer
 * resolves an attribute to an *editor* by name — the same arrangement `r-list`
 * uses.
 *
 * Nothing here duplicates a column style attribute. Spacing, colour and
 * typography come from Column Styles on the `<details>` element and inherit
 * into the summary and the detail; the attributes below are only what is
 * specific to a disclosure — what it says closed, what it says open, whether
 * it starts open, and whether the summary also belongs in the page outline.
 */
export const ACCORDION_SUMMARY_ATTRIBUTE = 'accordion-summary-3-resolutions';
export const ACCORDION_DETAIL_ATTRIBUTE = 'accordion-detail-3-resolutions';
export const ACCORDION_LINKS_ATTRIBUTE = 'accordion-links-3-resolutions';
export const ACCORDION_INLINE_STYLES_ATTRIBUTE = 'accordion-inline-styles-3-resolutions';
export const ACCORDION_OPEN_ATTRIBUTE = 'accordion-open-3-resolutions';
export const ACCORDION_HEADING_LEVEL_ATTRIBUTE = 'accordion-heading-level-3-resolutions';

/** What a freshly dropped accordion shows before the author types anything. */
export const DEFAULT_ACCORDION_SUMMARY = 'Summary';
export const DEFAULT_ACCORDION_DETAIL =
  'The detail this summary expands to. Each line becomes its own paragraph.';

/**
 * Heading levels the summary can be given, plus `none` for the common case.
 *
 * `h1` is deliberately absent: the page already has one, and ten disclosures
 * down the page would otherwise offer ten ways to compete with it. Levels
 * start at `h2` because a summary sits under whatever heading introduces the
 * section it is in.
 *
 * `none` is a real option rather than an unset value so the picker always has
 * a definite selection — there is no way to clear a select back to nothing —
 * and it is the default because a disclosure is not automatically part of the
 * document outline.
 */
export const ACCORDION_HEADING_LEVELS = ['none', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
export type AccordionHeadingLevel = (typeof ACCORDION_HEADING_LEVELS)[number];
export const DEFAULT_ACCORDION_HEADING_LEVEL: AccordionHeadingLevel = 'none';

type Attributes = { [key: string]: any } | undefined;

/**
 * The summary, on one line. An unset attribute falls back to the placeholder,
 * but text the author deliberately cleared stays cleared: an empty `<summary>`
 * still draws its chevron and still toggles, and the composer needs the element
 * to stay selectable on the canvas. Same distinction `r-list` draws for its
 * items and `r-button` for its label.
 *
 * Authored newlines collapse to single spaces. The editor behind this
 * attribute is the shared multi-line text field, and `<summary>` is a single
 * line of prose — HTML would collapse the whitespace anyway, so the resolved
 * value says so rather than leaving it to the browser.
 *
 * `{n|…}` markers contribute their inner text and nothing else. The summary
 * takes no links — inside a `<summary>` a click both follows the anchor and
 * toggles the disclosure — but the editor behind it is the same field that
 * offers the marker grammar for the detail, so an author will type one here.
 * Degrading it to prose is what the rest of the library does with a marker it
 * cannot resolve; leaving the braces on screen is the one outcome nobody wants.
 */
export const resolveAccordionSummary = (componentAttributes: Attributes): string => {
  const value = componentAttributes?.[ACCORDION_SUMMARY_ATTRIBUTE];
  const text =
    value === undefined || value === null
      ? DEFAULT_ACCORDION_SUMMARY
      : typeof value === 'string'
        ? value
        : String(value);
  return parseInlineLinkMarkers(text)
    .map((run) => run.text)
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * The detail, as one paragraph per authored line with blank lines dropped —
 * the same grammar `r-list` uses for its items, so an author who has met one
 * has met both. An unset attribute falls back to the placeholder; text the
 * author deliberately cleared stays cleared and the disclosure opens onto
 * whatever else is in it, or onto nothing.
 */
export const resolveAccordionDetail = (componentAttributes: Attributes): string[] => {
  const value = componentAttributes?.[ACCORDION_DETAIL_ATTRIBUTE];
  const text =
    value === undefined || value === null
      ? DEFAULT_ACCORDION_DETAIL
      : typeof value === 'string'
        ? value
        : String(value);
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
};

/**
 * Whether the disclosure renders already open. Select editors in the composer
 * store `'true'` / `'false'` as strings, while a hand-written or migrated
 * config may carry a real boolean; both mean the same thing here.
 *
 * This is an attribute rather than runtime state on purpose: `open` has to be
 * right in the server's HTML, because a disclosure a search engine or a
 * no-JavaScript reader should see expanded cannot wait for hydration.
 */
export const resolveAccordionOpen = (componentAttributes: Attributes): boolean => {
  const value = componentAttributes?.[ACCORDION_OPEN_ATTRIBUTE];
  if (typeof value === 'boolean') {
    return value;
  }
  return typeof value === 'string' && value.trim().toLowerCase() === 'true';
};

/**
 * The heading tag to wrap the summary in, or `undefined` for no heading.
 *
 * A stored value reaches an element name here, so the allowlist is the
 * boundary that keeps it from being whatever a request happened to write.
 * `none` and anything unrecognised both mean "no heading", which is also the
 * safe answer: a plain `<summary>` is a valid disclosure, while an invented
 * tag inside one is not.
 */
export const resolveAccordionHeadingLevel = (
  componentAttributes: Attributes
): Exclude<AccordionHeadingLevel, 'none'> | undefined => {
  const value = componentAttributes?.[ACCORDION_HEADING_LEVEL_ATTRIBUTE];
  if (value === 'none' || !(ACCORDION_HEADING_LEVELS as readonly unknown[]).includes(value)) {
    return undefined;
  }
  return value;
};
